// Generisches Trainings-Metadatenmodell (Parallel zu exercise_metadata_*)

import { getSupabaseClient } from "../supabase/index.js";

export const TENNIS_SPORT_KEY = "tennis";
export const DEFAULT_METADATA_LOCALE = "de-DE";

function toSport(row) {
  if (!row) return null;
  return {
    id: row.id,
    sportKey: row.sport_key,
    label: row.label,
    description: row.description || "",
    sortOrder: row.sort_order ?? 0,
    isActive: row.is_active !== false,
  };
}

function toDimension(row, locale = DEFAULT_METADATA_LOCALE) {
  if (!row) return null;
  return {
    id: row.id,
    sportId: row.sport_id,
    sportKey: row.sport?.sport_key || row.sport_key || "",
    dimensionKey: row.dimension_key,
    label: row.label,
    description: row.description || "",
    cardinality: row.cardinality || "multi",
    sortOrder: row.sort_order ?? 0,
    isActive: row.is_active !== false,
    legacyGroupKey: row.legacy_group_key || null,
    locale,
  };
}

function toItem(row, locale = DEFAULT_METADATA_LOCALE) {
  if (!row) return null;
  const i18n = Array.isArray(row.i18n) ? row.i18n : [];
  const localized = i18n.find((e) => e.locale === locale) || i18n[0] || null;
  const dimension = row.dimension || null;
  return {
    id: row.id,
    dimensionId: row.dimension_id,
    dimensionKey: dimension?.dimension_key || row.dimension_key || "",
    itemKey: row.item_key,
    label: localized?.label || row.item_key,
    description: localized?.description || row.description || "",
    sortOrder: row.sort_order ?? 0,
    isActive: row.is_active !== false,
    legacyMetadataItemId: row.legacy_metadata_item_id || null,
    locale,
  };
}

/**
 * @param {string} [sportKey]
 */
export async function loadSports(sportKey) {
  try {
    const sb = await getSupabaseClient();
    if (!sb) return { ok: false, error: "no-supabase" };
    let q = sb
      .from("sports")
      .select("*")
      .eq("is_active", true)
      .order("sort_order");
    if (sportKey) q = q.eq("sport_key", sportKey);
    const { data, error } = await q;
    if (error) return { ok: false, error };
    return { ok: true, data: (data || []).map(toSport).filter(Boolean) };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabase]
 * @param {{ sportKey?: string, includeInactive?: boolean, locale?: string }} [opts]
 */
export async function loadMetadataDimensions(supabase, opts = {}) {
  try {
    const sb = supabase || (await getSupabaseClient());
    if (!sb) return { ok: false, error: "no-supabase" };
    const sportKey = String(opts.sportKey || TENNIS_SPORT_KEY).trim();
    const locale = String(opts.locale || DEFAULT_METADATA_LOCALE).trim();

    const { data: sportRow, error: sportErr } = await sb
      .from("sports")
      .select("id")
      .eq("sport_key", sportKey)
      .maybeSingle();
    if (sportErr) return { ok: false, error: sportErr };
    if (!sportRow?.id) return { ok: true, data: [] };

    let q = sb
      .from("metadata_dimensions")
      .select("*")
      .eq("sport_id", sportRow.id)
      .order("sort_order")
      .order("label");
    if (!opts.includeInactive) q = q.eq("is_active", true);

    const { data, error } = await q;
    if (error) return { ok: false, error };
    return {
      ok: true,
      data: (data || []).map((row) =>
        toDimension({ ...row, sport_key: sportKey }, locale),
      ),
    };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabase]
 * @param {{ sportKey?: string, dimensionKey?: string, includeInactive?: boolean, locale?: string }} [opts]
 */
export async function loadMetadataItems(supabase, opts = {}) {
  try {
    const sb = supabase || (await getSupabaseClient());
    if (!sb) return { ok: false, error: "no-supabase" };
    const sportKey = String(opts.sportKey || TENNIS_SPORT_KEY).trim();
    const dimensionKey = String(opts.dimensionKey || "").trim();
    const locale = String(opts.locale || DEFAULT_METADATA_LOCALE).trim();

    const { data: sportRow, error: sportErr } = await sb
      .from("sports")
      .select("id")
      .eq("sport_key", sportKey)
      .maybeSingle();
    if (sportErr) return { ok: false, error: sportErr };
    if (!sportRow?.id) return { ok: true, data: [], itemsByDimension: {} };

    let dimQ = sb
      .from("metadata_dimensions")
      .select("id, dimension_key")
      .eq("sport_id", sportRow.id);
    if (dimensionKey) dimQ = dimQ.eq("dimension_key", dimensionKey);
    if (!opts.includeInactive) dimQ = dimQ.eq("is_active", true);
    const { data: dimensions, error: dimErr } = await dimQ;
    if (dimErr) return { ok: false, error: dimErr };

    const dimIds = (dimensions || []).map((d) => d.id).filter(Boolean);
    const dimKeyById = new Map(
      (dimensions || []).map((d) => [d.id, d.dimension_key]),
    );
    if (dimIds.length === 0)
      return { ok: true, data: [], itemsByDimension: {} };

    let itemQ = sb
      .from("metadata_items")
      .select(
        `
        *,
        i18n:metadata_item_i18n(locale, label, description)
      `,
      )
      .in("dimension_id", dimIds)
      .order("sort_order")
      .order("item_key");
    if (!opts.includeInactive) itemQ = itemQ.eq("is_active", true);

    const { data, error } = await itemQ;
    if (error) return { ok: false, error };

    const items = (data || []).map((row) =>
      toItem(
        {
          ...row,
          dimension_key: dimKeyById.get(row.dimension_id),
        },
        locale,
      ),
    );

    const itemsByDimension = items.reduce((acc, item) => {
      const key = item.dimensionKey;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, /** @type {Record<string, object[]>} */ ({}));

    return { ok: true, data: items, itemsByDimension };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabase]
 * @param {{ sportKey?: string, locale?: string, includeInactive?: boolean }} [opts]
 */
let __catalogApiInflight = null;

export async function loadTrainingMetadataCatalogFromApi(opts = {}) {
  const cacheKey = JSON.stringify({
    sport: opts.sportKey || TENNIS_SPORT_KEY,
    locale: opts.locale || DEFAULT_METADATA_LOCALE,
    includeInactive: !!opts.includeInactive,
  });
  if (__catalogApiInflight?.key === cacheKey) {
    return __catalogApiInflight.promise;
  }

  const promise = (async () => {
    try {
      const { fetchApiWithAuth } =
        await import("../api/fetch-api-with-auth.js");
      const sport = String(opts.sportKey || TENNIS_SPORT_KEY).trim();
      const locale = String(opts.locale || DEFAULT_METADATA_LOCALE).trim();
      const params = new URLSearchParams({ sport, locale });
      if (opts.includeInactive) params.set("includeInactive", "1");
      const res = await fetchApiWithAuth(
        `/api/metadata/catalog?${params.toString()}`,
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.ok === false) {
        const reason = body.reason || `http-${res.status}`;
        if (reason === "schema-not-ready" || res.status === 503) {
          return {
            ok: true,
            data: { dimensions: [], items: [], itemsByDimension: {} },
          };
        }
        return {
          ok: false,
          error: reason,
          status: res.status,
        };
      }
      return {
        ok: true,
        data: {
          dimensions: body.dimensions || [],
          items: body.items || [],
          itemsByDimension: body.itemsByDimension || {},
        },
      };
    } catch (error) {
      return { ok: false, error };
    } finally {
      if (__catalogApiInflight?.key === cacheKey) __catalogApiInflight = null;
    }
  })();

  __catalogApiInflight = { key: cacheKey, promise };
  return promise;
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabase]
 * @param {{ sportKey?: string, locale?: string, includeInactive?: boolean }} [opts]
 */
export async function loadTrainingMetadataCatalog(supabase, opts = {}) {
  if (typeof window !== "undefined" && typeof fetch !== "undefined") {
    return loadTrainingMetadataCatalogFromApi(opts);
  }

  const [dimensionsRes, itemsRes] = await Promise.all([
    loadMetadataDimensions(supabase, opts),
    loadMetadataItems(supabase, {
      ...opts,
      includeInactive: opts.includeInactive !== false,
    }),
  ]);
  if (!dimensionsRes.ok) return dimensionsRes;
  if (!itemsRes.ok) return itemsRes;
  return {
    ok: true,
    data: {
      dimensions: dimensionsRes.data || [],
      items: itemsRes.data || [],
      itemsByDimension: itemsRes.itemsByDimension || {},
    },
  };
}

/**
 * @param {string} drillId
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabase]
 * @param {{ locale?: string }} [opts]
 */
export async function loadDrillMetadata(drillId, supabase, opts = {}) {
  try {
    const sb = supabase || (await getSupabaseClient());
    if (!sb) return { ok: false, error: "no-supabase" };
    const id = String(drillId || "").trim();
    if (!id) return { ok: false, error: "id-required" };
    const locale = String(opts.locale || DEFAULT_METADATA_LOCALE).trim();

    const { data, error } = await sb
      .from("drill_metadata_links")
      .select(
        `
        metadata_item_id,
        item:metadata_items(
          id,
          item_key,
          sort_order,
          is_active,
          legacy_metadata_item_id,
          i18n:metadata_item_i18n(locale, label, description),
          dimension:metadata_dimensions(
            dimension_key,
            label,
            cardinality,
            sport:sports(sport_key)
          )
        )
      `,
      )
      .eq("drill_id", id);

    if (error) return { ok: false, error };

    const items = (data || [])
      .map((row) => {
        const mapped = toItem(row.item, locale);
        if (!mapped) return null;
        return {
          ...mapped,
          sportKey: row.item?.dimension?.sport?.sport_key || TENNIS_SPORT_KEY,
          dimensionLabel: row.item?.dimension?.label || mapped.dimensionKey,
          cardinality: row.item?.dimension?.cardinality || "multi",
        };
      })
      .filter(Boolean);

    const byDimension = items.reduce((acc, item) => {
      const key = item.dimensionKey;
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    }, /** @type {Record<string, object[]>} */ ({}));

    return { ok: true, data: { drillId: id, items, byDimension } };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * Dual-Write: drill_metadata_links aus Legacy-Tags + strukturellen Feldern.
 * @param {string} drillId
 * @param {object} drill
 * @param {string[]} [legacyTagMetadataIds] exercise_metadata_items IDs
 * @param {import('@supabase/supabase-js').SupabaseClient} [supabase]
 */
export async function syncDrillMetadataLinksDualWrite(
  drillId,
  drill,
  legacyTagMetadataIds = [],
  supabase,
) {
  try {
    const sb = supabase || (await getSupabaseClient());
    if (!sb || !drillId) return { ok: false, error: "no-supabase" };

    const newItemIds = new Set();

    for (const legacyId of legacyTagMetadataIds || []) {
      if (!legacyId) continue;
      const { data } = await sb
        .from("metadata_items")
        .select("id")
        .eq("legacy_metadata_item_id", legacyId)
        .maybeSingle();
      if (data?.id) newItemIds.add(data.id);
    }

    const { data: sportRow } = await sb
      .from("sports")
      .select("id")
      .eq("sport_key", TENNIS_SPORT_KEY)
      .maybeSingle();

    const structural = [
      { dimensionKey: "category", value: drill?.category },
      { dimensionKey: "skill_level", value: drill?.level },
      {
        dimensionKey: "difficulty",
        value: drill?.difficulty != null ? String(drill.difficulty) : null,
      },
    ];

    if (sportRow?.id) {
      for (const entry of structural) {
        const value = String(entry.value || "").trim();
        if (!value) continue;
        const { data: dim } = await sb
          .from("metadata_dimensions")
          .select("id")
          .eq("sport_id", sportRow.id)
          .eq("dimension_key", entry.dimensionKey)
          .maybeSingle();
        if (!dim?.id) continue;
        const { data: itemRow } = await sb
          .from("metadata_items")
          .select("id")
          .eq("dimension_id", dim.id)
          .eq("item_key", value)
          .maybeSingle();
        if (itemRow?.id) newItemIds.add(itemRow.id);
      }
    }

    await sb.from("drill_metadata_links").delete().eq("drill_id", drillId);

    const ids = [...newItemIds];
    if (ids.length > 0) {
      const { error } = await sb
        .from("drill_metadata_links")
        .insert(
          ids.map((metadataItemId) => ({
            drill_id: drillId,
            metadata_item_id: metadataItemId,
          })),
        );
      if (error) return { ok: false, error };
    }

    return { ok: true, count: ids.length };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * @param {string} itemId
 * @param {{ label?: string, description?: string, sortOrder?: number, isActive?: boolean, locale?: string }} patch
 */
export async function updateMetadataItem(itemId, patch) {
  try {
    const sb = await getSupabaseClient();
    if (!sb) return { ok: false, error: "no-supabase" };
    const id = String(itemId || "").trim();
    if (!id) return { ok: false, error: "id-required" };
    const locale = String(patch.locale || DEFAULT_METADATA_LOCALE).trim();

    const itemRow = {};
    if (patch.description != null) itemRow.description = patch.description;
    if (patch.sortOrder != null) itemRow.sort_order = Number(patch.sortOrder);
    if (patch.isActive != null) itemRow.is_active = !!patch.isActive;
    if (Object.keys(itemRow).length > 0) {
      itemRow.updated_at = new Date().toISOString();
      const { error } = await sb
        .from("metadata_items")
        .update(itemRow)
        .eq("id", id);
      if (error) return { ok: false, error };
    }

    if (patch.label != null) {
      const { error } = await sb.from("metadata_item_i18n").upsert(
        [
          {
            item_id: id,
            locale,
            label: String(patch.label).trim(),
            description:
              patch.description != null ? String(patch.description) : "",
            updated_at: new Date().toISOString(),
          },
        ],
        { onConflict: "item_id,locale" },
      );
      if (error) return { ok: false, error };
    }

    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * @param {string} dimensionId
 * @param {{ sortOrder?: number, isActive?: boolean, label?: string, description?: string }} patch
 */
export async function updateMetadataDimension(dimensionId, patch) {
  try {
    const sb = await getSupabaseClient();
    if (!sb) return { ok: false, error: "no-supabase" };
    const id = String(dimensionId || "").trim();
    if (!id) return { ok: false, error: "id-required" };

    const row = {};
    if (patch.label != null) row.label = String(patch.label).trim();
    if (patch.description != null) row.description = patch.description;
    if (patch.sortOrder != null) row.sort_order = Number(patch.sortOrder);
    if (patch.isActive != null) row.is_active = !!patch.isActive;
    row.updated_at = new Date().toISOString();

    const { error } = await sb
      .from("metadata_dimensions")
      .update(row)
      .eq("id", id);
    if (error) return { ok: false, error };
    return { ok: true };
  } catch (error) {
    return { ok: false, error };
  }
}
